/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import type { Ad, AdInfo, AdBreak, AdBreakInfo, NetworkRequestObservation } from '@quickplay/player-interface';
import type { AnalyticsReporter } from '../AnalyticsReporter';
import { PlaybackEvent } from '../../../../event/PlaybackEvent';
import { ApplicationEvent } from '../../../../event/ApplicationEvent';
import { AdPlaybackEvent } from '../../../../event/AdPlaybackEvent';
import type { GodavariContentId, GodavariMetadata, GodavariMetadataInput } from './GodavariMetadata';
import type {
  GodavariEventTime,
  GodavariHeartbeatFields,
  GodavariPulseObservation,
  GodavariEndReason,
} from './GodavariWirePayload';

export type GodavariEventInput = Omit<GodavariMetadataInput, 'event_exception' | 'event_error_code'> & {
  [K in Exclude<keyof GodavariHeartbeatFields, keyof GodavariEventTime>]?: number | string;
} & Partial<Omit<AdInfo & Ad, 'adBreakInfo' | 'adBreak'>> & {
    end_reason?: GodavariEndReason;
    adBreakInfo?: Partial<AdBreakInfo>;
    adBreak?: Partial<AdBreak>;
    event_exception?: unknown;
    event_error_code?: string | number;
    errorCode?: string | number;
    errorMessage?: string;
    exception?: unknown;
    playback_error_code?: string | number;
    playback_error_message?: string;
    playback_error_internal?: unknown;
    content_playhead_position_ms?: number;
    audio_language?: string;
    subtitle_language?: string;
  };

export type GodavariEventMetadata = Omit<
  GodavariEventInput,
  keyof GodavariMetadataInput | keyof GodavariHeartbeatFields
> &
  GodavariMetadata &
  Partial<Omit<GodavariHeartbeatFields, keyof GodavariEventTime>>;

export interface GodavariObservationEvents {
  Pulse: GodavariPulseObservation;
  BufferHealthSample: { position: number; bufferedAhead: number };
  ManifestParsing: undefined;
  ManifestParsed: undefined;
  NetworkRequest: NetworkRequestObservation;
  NetworkResponse: NetworkRequestObservation;
  VideoAttempt: undefined;
  [PlaybackEvent.PLAYBACK_START]: undefined;
  [PlaybackEvent.PLAYBACK_STARTED]: undefined;
}

export type GodavariObservationArgs = {
  [K in keyof GodavariObservationEvents]: GodavariObservationEvents[K] extends undefined
    ? [event: K]
    : [event: K, payload: GodavariObservationEvents[K]];
}[keyof GodavariObservationEvents];

export type GodavariLifecycleObservation = {
  [K in keyof GodavariObservationEvents]: GodavariObservationEvents[K] extends undefined ? K : never;
}[keyof GodavariObservationEvents];

export type GodavariMetadataEvent = Exclude<
  | ApplicationEvent
  | PlaybackEvent
  | AdPlaybackEvent
  | 'AppBackgrounded'
  | 'AppForegrounded'
  | 'AdBufferStart'
  | 'AdBufferEnd'
  | 'vmaprequeststart'
  | 'vmaprequestend'
  | 'AdHeartbeat',
  keyof GodavariObservationEvents
>;

/**
 * Metadata supplied inline with `playback_request` must identify the content.
 * The event may omit inline metadata when `video_content_id` was already set
 * through playback metadata or the reporter configuration.
 */
export type GodavariPlaybackRequestInput = GodavariEventInput & {
  video_content_id: GodavariContentId;
};

/** A bitrate-change event needs the new bitrate; the previous value is tracked by the reporter. */
export type GodavariBitrateChangeInput = GodavariEventInput &
  (
    | { video_current_bitrate: number; event_to_bitrate?: number }
    | { video_current_bitrate?: number; event_to_bitrate: number }
  );

/** A language change needs the next language; the previous value is tracked by the reporter. */
export type GodavariAudioLanguageChangeInput = GodavariEventInput &
  (
    | { video_audio_language: string; audio_language?: string }
    | { video_audio_language?: never; audio_language: string }
  );

/** A subtitle change needs the next language; the previous value is tracked by the reporter. */
export type GodavariSubtitleLanguageChangeInput = GodavariEventInput &
  (
    | { video_subtitle_language: string; subtitle_language?: string }
    | { video_subtitle_language?: never; subtitle_language: string }
  );

interface GodavariEventRequiredMetadata {
  [PlaybackEvent.PLAYBACK_REQUEST]: GodavariPlaybackRequestInput;
  [PlaybackEvent.PLAYBACK_VARIANT_CHANGE]: GodavariBitrateChangeInput;
  [PlaybackEvent.PLAYBACK_AUDIO_LANGUAGE_CHANGE]: GodavariAudioLanguageChangeInput;
  [PlaybackEvent.PLAYBACK_SUBTITLE_LANGUAGE_CHANGE]: GodavariSubtitleLanguageChangeInput;
}

/**
 * Caller-provided metadata by event. Events without special input requirements
 * accept optional metadata; reporter-generated wire fields are described by
 * `GodavariLifecyclePayloads` and `GodavariBatchPayloads`.
 */
export type GodavariEventMetadataByName = {
  [K in GodavariMetadataEvent]: K extends keyof GodavariEventRequiredMetadata
    ? GodavariEventRequiredMetadata[K]
    : GodavariEventInput;
};

/** Typed event arguments for integrations that report directly to a reporter. */
export type GodavariEventArgs = {
  [K in GodavariMetadataEvent]: K extends keyof GodavariEventRequiredMetadata
    ? K extends PlaybackEvent.PLAYBACK_REQUEST
      ? [event: K] | [event: K, metadata: GodavariEventRequiredMetadata[K]]
      : [event: K, metadata: GodavariEventRequiredMetadata[K]]
    : [event: K] | [event: K, metadata?: GodavariEventMetadataByName[K]];
}[GodavariMetadataEvent];

function reportTypedEvent(reporter: AnalyticsReporter, args: [event: string, metadata?: object]): void {
  if (args[1] === undefined) {
    reporter.reportEvent(args[0]);
  } else {
    reporter.reportEvent(args[0], args[1]);
  }
}

/** Reports an event using its event-specific metadata contract. */
export function reportGodavariEvent(reporter: AnalyticsReporter, ...args: GodavariEventArgs): void {
  reportTypedEvent(reporter, args);
}

export function reportGodavariObservation(reporter: AnalyticsReporter, ...args: GodavariObservationArgs): void {
  reportTypedEvent(reporter, args);
}
